import React, {useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReviewFeedbackForm from '../components/reviews/ReviewFeedbackForm';
import {getReviewLink} from '../services/reviewLinkService';

const {FiSmile, FiMeh, FiFrown, FiImage} = FiIcons;
const choices = [
  {label: 'Excellent', icon: FiSmile, color: '#19c987', emoji: '😄', rating: 5},
  {label: 'Good', icon: FiSmile, color: '#3485e8', emoji: '🙂', rating: 4},
  {label: 'OK', icon: FiMeh, color: '#ffbb35', emoji: '😐', rating: 3},
  {label: 'Bad', icon: FiFrown, color: '#f27052', emoji: '😟', rating: 2}
];

function ExperienceChoice({choice, onClick}) {
  return (
    <button type="button" onClick={onClick} className="group relative flex min-h-32 flex-col justify-end overflow-hidden rounded-xl p-4 text-left text-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-[#2563eb] sm:min-h-36" style={{backgroundColor: choice.color}}>
      <span className="absolute left-4 top-3 opacity-25"><SafeIcon icon={choice.icon} className="h-7 w-7" /></span>
      <span className="absolute -right-1 -top-4 text-[4.5rem] leading-none transition-transform duration-200 group-hover:rotate-12 sm:text-[5rem]" aria-hidden="true">{choice.emoji}</span>
      <span className="relative text-xl font-extrabold sm:text-2xl">{choice.label}</span>
    </button>
  );
}

export default function ReviewGatePage() {
  const {slug} = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setBusiness(null);
    setSelected(null);
    setLoadError('');
    getReviewLink(slug)
      .then((record) => {if (active) setBusiness(record);})
      .catch(() => {if (active) setLoadError('This page could not be loaded right now.');})
      .finally(() => {if (active) setLoading(false);});
    return () => {active = false;};
  }, [slug]);

  function handleChoice(choice) {
    if (choice.rating === 5) {
      if (business?.place_id) {
        window.location.assign(`https://search.google.com/local/writereview?placeid=${encodeURIComponent(business.place_id)}`);
      }
      return;
    }
    setSelected(choice);
  }

  return (
    <main className="min-h-screen bg-white px-5 py-16 text-[#0b1423] sm:px-8 sm:py-24 lg:py-32">
      <div className="mx-auto max-w-6xl">
        {loading && <p className="text-center text-slate-600">Loading business…</p>}
        {!loading && (loadError || !business) && (
          <div className="text-center">
            <h1 className="text-3xl font-black">Page unavailable</h1>
            <p role="alert" className="mt-3 text-slate-600">{loadError || 'We could not find this business.'}</p>
            <Link to="/reviews" className="mt-6 inline-block font-bold text-[#2563eb]">Find a business</Link>
          </div>
        )}
        {business && !loading && (
          <>
            <div className="grid items-center gap-10 md:grid-cols-[0.8fr_1.2fr] md:gap-16">
              <div className="flex min-h-44 items-center justify-center overflow-hidden rounded-2xl bg-[#f5f8fc] p-3 sm:min-h-56 sm:p-4">
                {business.image_url ? <img src={business.image_url} alt={`${business.business_name} business photo`} className="max-h-72 w-full rounded-2xl object-contain" /> : <SafeIcon icon={FiImage} className="h-16 w-16 text-[#2563eb]" />}
              </div>
              <div>
                <h1 className="text-4xl font-black leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">How was your experience with {business.business_name}?</h1>
                <p className="mt-8 text-xl text-slate-700 sm:mt-10 sm:text-2xl">Your opinion matters to us, thanks!</p>
              </div>
            </div>
            {selected ? (
              <ReviewFeedbackForm business={business} rating={selected.rating} onBack={() => setSelected(null)} />
            ) : (
              <>
                <div role="group" aria-label="How was your experience?" className="mx-auto mt-16 grid max-w-4xl grid-cols-2 gap-4 sm:mt-20 sm:grid-cols-4 sm:gap-7">
                  {choices.map((choice) => <ExperienceChoice key={choice.label} choice={choice} onClick={() => handleChoice(choice)} />)}
                </div>
                <p className="mt-14 text-center text-lg font-bold sm:mt-16 sm:text-xl">Choose an option to continue.</p>
              </>
            )}
          </>
        )}
      </div>
    </main>
  );
}